import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { Observable } from 'rxjs';
import type {
  BdJobChainStatus,
  BdJobChainSummaryResponse,
  BdJobLane,
  BdJobQueueStatusResponse,
  BdJobSubscriptionStatusResponse,
  BdJobTaskStatus,
  BdJobTaskStatusResponse,
} from './chain.model';
import { jobQueryParams, jobRequest, jobsBaseUrl } from './job-chain.api';

export interface BdJobAdminChainQuery {
  kind?: string;
  status?: BdJobChainStatus;
  /** User id. */
  createdBy?: string;
  limit?: number;
}

export interface BdJobAdminTaskQuery {
  status?: BdJobTaskStatus;
  queue?: string;
  lane?: BdJobLane;
  type?: string;
  createdBy?: string;
  /** Chain id. */
  correlationId?: string;
  limit?: number;
}

/**
 * Admin surface of the job-status API: every user's chains and tasks with
 * the full `error`, queue and subscription health. Needs `ADMIN:JOBS`
 * (included in `ADMIN:GOD`); 403 otherwise.
 */
@Injectable({ providedIn: 'root' })
export class BdJobAdminApi {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = jobsBaseUrl(inject(BdAppEnvironmentService));

  public chains(
    query: BdJobAdminChainQuery = {},
  ): Observable<BdJobChainSummaryResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobChainSummaryResponse[]>(`${base}/admin/chains`, {
        params: jobQueryParams({ ...query }),
      }),
    );
  }

  public tasks(
    query: BdJobAdminTaskQuery = {},
  ): Observable<BdJobTaskStatusResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobTaskStatusResponse[]>(`${base}/admin/tasks`, {
        params: jobQueryParams({ ...query }),
      }),
    );
  }

  /** 404 when the task is unknown. */
  public task(id: string): Observable<BdJobTaskStatusResponse> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobTaskStatusResponse>(
        `${base}/admin/tasks/${encodeURIComponent(id)}`,
      ),
    );
  }

  public queues(): Observable<BdJobQueueStatusResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobQueueStatusResponse[]>(`${base}/admin/queues`),
    );
  }

  public subscriptions(): Observable<BdJobSubscriptionStatusResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobSubscriptionStatusResponse[]>(
        `${base}/admin/subscriptions`,
      ),
    );
  }
}
